import { TableScroll } from '@web/components/table-scroll.tsx';
import { useReportPaging } from '@web/components/report-pagination.tsx';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { EmptyState, classes } from '@web/components/ui.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { formatMinutes } from '@web/i18n/format.ts';
import { getDateLocale } from '@web/i18n/locale.ts';
import type { ProfitabilityGroupRow } from '@shared/types/index.ts';
import {
  reportRetry,
  StatTile,
  ReportBoundary,
  SectionCard,
  CustomerLink,
  TH,
  THR,
  TD,
  TDR,
  ROW,
  type RangeParams,
} from '../shared.tsx';

export function ProfitabilitySection({ range, scope }: { range: RangeParams; scope?: 'all' }) {
  const { t } = useTranslation();
  const paging = useReportPaging({ ...range, scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'profitability', { ...range, scope, pages }, paging.identity],
    queryFn: () => reportsApi.profitability({ ...range, scope, pages }),
    retry: reportRetry,
  });

  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => {
        const money = (cents: number) => formatCents(cents, report.currencyCode, getDateLocale());
        const rate = (centsPerHour: number | null) =>
          centsPerHour === null ? '—' : t('reports.profitability.perHour', { rate: money(centsPerHour) });
        const totals = report.totals;

        return (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label={t('reports.profitability.realizedRate')}
                value={rate(totals.realizedRateCentsPerHour)}
                hint={t('reports.profitability.realizedRateHint', {
                  rate: t('reports.profitability.perHour', { rate: money(report.targetRateCentsPerHour) }),
                })}
              />
              <StatTile
                label={t('reports.profitability.billed')}
                value={money(totals.billedCents)}
                hint={t('reports.profitability.billedHint', { count: totals.completedVisits })}
              />
              <StatTile
                label={t('reports.profitability.collected')}
                value={money(totals.collectedCents)}
                hint={t('reports.profitability.collectedHint')}
              />
              {/*
                Person-hours, with the time on site named beside them only
                when a crew makes the two differ — on a one-person company
                they are the same figure, and saying it twice reads as an
                error rather than a distinction.
              */}
              <StatTile
                label={t('reports.profitability.timeWorked')}
                value={formatMinutes(totals.laborMinutes)}
                hint={t(
                  totals.laborMinutes === totals.actualMinutes
                    ? 'reports.profitability.timeWorkedHint'
                    : 'reports.profitability.timeWorkedHintCrew',
                  {
                    tracked: totals.trackedVisits,
                    total: totals.completedVisits,
                    onSite: formatMinutes(totals.actualMinutes),
                  },
                )}
              />
            </div>

            {totals.completedVisits === 0 ? (
              <EmptyState title={t('reports.profitability.emptyTitle')}>
                {t('reports.profitability.emptyBody')}
              </EmptyState>
            ) : (
              <div className="space-y-6">
                <SectionCard
                  title={t('reports.profitability.byCustomerTitle')}
                  collection="byCustomer"
                  hint={t('reports.profitability.byCustomerHint')}
                  csvHref={reportsApi.csvUrl('profitability', { ...range, scope })}
                >
                  <ProfitabilityTable rows={report.byCustomer} money={money} rate={rate} kind="customer" />
                  {report.byCustomerTruncated ? (
                    <p className="mt-2 text-sm text-[color:var(--color-muted)]">
                      {t('reports.profitability.truncated', { count: report.byCustomer.length })}
                    </p>
                  ) : null}
                </SectionCard>
                {/*
                  One row per building, for the accounts that have more than
                  one. Placed above the schedule breakdown because on a
                  multi-site contract the building is the level a problem
                  actually lives at, and a single row per client hides it.
                */}
                {report.bySite.length > 1 || (report.pagination?.bySite?.page ?? 1) > 1 ? (
                  <SectionCard
                    title={t('reports.profitability.bySiteTitle')}
                    collection="bySite"
                    hint={t('reports.profitability.bySiteHint')}
                  >
                    <ProfitabilityTable rows={report.bySite} money={money} rate={rate} kind="site" />
                    {report.bySiteTruncated ? (
                      <p className="mt-2 text-sm text-[color:var(--color-muted)]">
                        {t('reports.profitability.truncated', { count: report.bySite.length })}
                      </p>
                    ) : null}
                  </SectionCard>
                ) : null}
                {report.bySeries.length > 0 || (report.pagination?.bySeries?.page ?? 1) > 1 ? (
                  <SectionCard
                    title={t('reports.profitability.bySeriesTitle')}
                    collection="bySeries"
                    hint={t('reports.profitability.bySeriesHint')}
                  >
                    <ProfitabilityTable rows={report.bySeries} money={money} rate={rate} kind="series" />
                    {report.bySeriesTruncated ? (
                      <p className="mt-2 text-sm text-[color:var(--color-muted)]">
                        {t('reports.profitability.truncated', { count: report.bySeries.length })}
                      </p>
                    ) : null}
                  </SectionCard>
                ) : null}
              </div>
            )}
          </>
        );
      }}
    </ReportBoundary>
  );
}

function ProfitabilityTable({
  rows,
  money,
  rate,
  kind,
}: {
  rows: ProfitabilityGroupRow[];
  money: (cents: number) => string;
  rate: (centsPerHour: number | null) => string;
  kind: 'customer' | 'series' | 'site';
}) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return <p className="text-[color:var(--color-muted)]">{t('reports.profitability.tableEmpty')}</p>;
  }
  return (
    <TableScroll
      aria-label={t(
        kind === 'customer'
          ? 'reports.profitability.byCustomerTitle'
          : kind === 'site'
            ? 'reports.profitability.bySiteTitle'
            : 'reports.profitability.bySeriesTitle',
      )}
      className="overflow-x-auto"
    >
      <table className="data-table w-full min-w-max border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-[color:var(--color-line)]">
            <th scope="col" className={TH}>
              {t('reports.profitability.colCustomer')}
            </th>
            {kind === 'series' ? (
              <th scope="col" className={TH}>
                {t('reports.profitability.colFrequency')}
              </th>
            ) : null}
            {kind === 'site' ? (
              <th scope="col" className={TH}>
                {t('reports.profitability.colSite')}
              </th>
            ) : null}
            <th scope="col" className={THR}>
              {t('reports.profitability.colVisits')}
            </th>
            <th scope="col" className={THR}>
              {t('reports.profitability.colTimeWorked')}
            </th>
            <th scope="col" className={THR}>
              {t('reports.profitability.colBilled')}
            </th>
            <th scope="col" className={THR}>
              {t('reports.profitability.colCollected')}
            </th>
            <th scope="col" className={THR}>
              {t('reports.profitability.colRate')}
            </th>
            <th scope="col" className={TH}>
              <span className="sr-only">{t('reports.profitability.colFlag')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={classes(ROW, row.belowTarget && 'bg-[color:var(--color-danger-soft)]')}>
              <td className={TD}>
                {kind === 'customer' ? (
                  <CustomerLink id={row.id} name={row.customerName} />
                ) : kind === 'site' ? (
                  // The client, plain: a site row's link belongs on the site
                  // column beside it, which is the thing this row is about.
                  <span className="block max-w-[18rem] table-text">{row.customerName}</span>
                ) : (
                  <Link
                    to={`/schedule/series/${row.id}`}
                    title={row.customerName}
                    className="inline-block max-w-[18rem] table-text align-bottom text-[color:var(--color-brand)] hover:underline"
                  >
                    {row.customerName}
                  </Link>
                )}
              </td>
              {kind === 'site' ? (
                <td className={TD}>
                  {/* Bounded, but wrapping rather than truncated: unlike the
                      customer and series columns this is neither a link nor
                      titled, so a clipped suffix would be unrecoverable — and
                      two buildings sharing a prefix would read alike. */}
                  <span className="table-text inline-block max-w-[18rem] align-bottom">
                    {row.siteName || t('reports.profitability.siteUnnamed')}
                  </span>
                  {row.siteCode ? <span className="ml-2 text-[color:var(--color-muted)]">{row.siteCode}</span> : null}
                </td>
              ) : null}
              {kind === 'series' ? (
                <td className={TD}>{row.frequency ? t(`schedule.frequency.${row.frequency}`) : '—'}</td>
              ) : null}
              <td className={TDR}>{row.completedVisits}</td>
              <td className={TDR}>
                {/* The rate's denominator, with the visit span beneath it when a crew makes them differ. */}
                {formatMinutes(row.laborMinutes)}
                {row.laborMinutes !== row.actualMinutes ? (
                  <span className="block text-xs text-[color:var(--color-muted)]">
                    {t('reports.profitability.onSite', { time: formatMinutes(row.actualMinutes) })}
                  </span>
                ) : null}
              </td>
              <td className={TDR}>{money(row.billedCents)}</td>
              <td className={TDR}>{money(row.collectedCents)}</td>
              <td className={TDR}>{rate(row.realizedRateCentsPerHour)}</td>
              <td className={classes(TD, 'pl-3')}>
                {row.belowTarget ? (
                  <span className="badge border-[color:var(--color-danger)] text-[color:var(--color-danger)]">
                    {t('reports.profitability.belowTarget')}
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}
